import React, { useState, useRef, useEffect } from 'react';

interface MultiSelectDropdownProps {
  options: readonly string[] | string[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  className?: string;
  error?: boolean;
  disabled?: boolean;
  maxSelections?: number;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  selectedValues,
  onChange,
  placeholder = "Select options...",
  searchPlaceholder = "Search...",
  className = "",
  error = false,
  disabled = false,
  maxSelections
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filter options based on search term
  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchTerm('');
      }
    }
  };

  const handleOptionToggle = (option: string) => {
    if (selectedValues.includes(option)) {
      // Remove option
      onChange(selectedValues.filter(value => value !== option));
    } else {
      // Add option (check max selections)
      if (!maxSelections || selectedValues.length < maxSelections) {
        onChange([...selectedValues, option]);
      }
    }
  };

  const handleSelectAll = () => {
    if (maxSelections) {
      onChange(filteredOptions.slice(0, maxSelections));
    } else {
      onChange([...filteredOptions]);
    }
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const handleRemoveTag = (optionToRemove: string) => {
    onChange(selectedValues.filter(value => value !== optionToRemove));
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      }
    }
  };


  return (
    <div className={`multi-select-dropdown ${className}`} ref={dropdownRef}>
      {/* Main dropdown trigger */}
      <div
        className={`multi-select-trigger ${error ? 'error' : ''} ${disabled ? 'disabled' : ''} ${isOpen ? 'open' : ''}`}
        onClick={handleToggleDropdown}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={placeholder}
      >
        <div className="multi-select-content">
          {/* Selected items as tags inside the trigger */}
          {selectedValues.length > 0 ? (
            <div className="multi-select-tags-inline">
              {selectedValues.map((value) => (
                <span key={value} className="multi-select-tag-inline">
                  {value}
                  <button
                    type="button"
                    className="multi-select-tag-remove-inline"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveTag(value);
                    }}
                    aria-label={`Remove ${value}`}
                    disabled={disabled}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <span className="multi-select-display-text">
              {placeholder}
            </span>
          )}
        </div>
        <span className={`multi-select-arrow ${isOpen ? 'open' : ''}`}>
          ▼
        </span>
      </div>

      {/* Dropdown content */}
      {isOpen && (
        <div className="multi-select-dropdown-content" role="listbox">
          {/* Search input */}
          <div className="multi-select-search">
            <input
              ref={searchInputRef}
              type="text"
              className="multi-select-search-input"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Control buttons */}
          <div className="multi-select-controls">
            <button
              type="button"
              className="multi-select-control-btn"
              onClick={handleSelectAll}
              disabled={filteredOptions.length === 0 || (!!maxSelections && selectedValues.length >= maxSelections)}
            >
              Select All
            </button>
            <button
              type="button"
              className="multi-select-control-btn"
              onClick={handleClearAll}
              disabled={selectedValues.length === 0}
            >
              Clear All
            </button>
          </div>

          {/* Options list */}
          <div className="multi-select-options">
            {filteredOptions.length === 0 ? (
              <div className="multi-select-no-options">
                {searchTerm ? 'No matching options found' : 'No options available'}
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = selectedValues.includes(option);
                const isDisabled = maxSelections && !isSelected && selectedValues.length >= maxSelections;
                
                return (
                  <div
                    key={option}
                    className={`multi-select-option ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                    onClick={() => !isDisabled && handleOptionToggle(option)}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => !isDisabled && handleOptionToggle(option)}
                      disabled={!!isDisabled}
                      className="multi-select-checkbox"
                      tabIndex={-1}
                    />
                    <span className="multi-select-option-text">{option}</span>
                  </div>
                );
              })
            )}
          </div>

          {/* Max selections warning */}
          {maxSelections && selectedValues.length >= maxSelections && (
            <div className="multi-select-max-warning">
              Maximum {maxSelections} selections allowed
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;